import { Button, Card, Col, notification, Radio, Row, Spin, Tag, Tooltip } from "antd";
import { useEffect, useState } from "react";
import moment from "moment";
import { billingPlanQuotesApi, billingPlansApi } from "../../config/Api";
import { apiErrorMessage } from "../../services/ApiError";
import { payForPlan } from "../../services/RazorpayCheckout";

export const rupees = (amount) => `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`

/**
 * Plan cards with a Monthly / Yearly toggle. Paid plans show what the doctor would pay now
 * (full price, a renewal, or an upgrade with credit) and open Razorpay Checkout.
 *
 * current: the doctor's current plan info (from billing/current)
 * onFree: choose the Free plan (optional; hidden when not given)
 * onPaid: called after a payment is confirmed
 * onPending: called when checkout closed without a confirmed payment
 */
export const PlanOptions = ({ current, onFree, freeLoading, onPaid, onPending }) => {
    const [plans, setPlans] = useState([])
    const [discount, setDiscount] = useState(0)
    const [cycle, setCycle] = useState('monthly')
    const [quotes, setQuotes] = useState({})
    const [loading, setLoading] = useState(true)
    const [paying, setPaying] = useState(null)

    useEffect(() => {
        billingPlansApi()
            .then((res) => {
                setPlans(res.data.data)
                setDiscount(res.data.yearly_discount_percent)
            })
            .catch((err) => notification.error({ message: apiErrorMessage(err) }))
            .finally(() => setLoading(false))
    }, [])

    // what each paid plan costs this doctor now, for the chosen cycle
    useEffect(() => {
        billingPlanQuotesApi(cycle)
            .then((res) => setQuotes(Object.fromEntries(res.data.data.map((quote) => [quote.plan_id, quote]))))
            .catch((err) => notification.error({ message: apiErrorMessage(err) }))
    }, [cycle, current])

    const pay = (plan) => {
        setPaying(plan.id)
        payForPlan(plan.id, cycle)
            .then((result) => {
                if (result.paid) {
                    notification.success({ message: result.message, duration: 8 })
                    onPaid && onPaid()
                } else {
                    notification.info({ message: result.message, duration: 8 })
                    onPending && onPending()
                }
            })
            .catch((err) => notification.error({ message: apiErrorMessage(err, err?.message) }))
            .finally(() => setPaying(null))
    }

    const buttonFor = (plan) => {
        if (plan.is_free) {
            // keep a button on every card so they line up
            if (!onFree) {
                return (
                    <Tooltip title="You can switch to Free when your paid plan ends.">
                        <Button block disabled>Available when your plan ends</Button>
                    </Tooltip>
                )
            }
            const blocked = current && !current.can_choose_free
            return (
                <Tooltip title={blocked ? 'You can switch to Free when your paid plan ends.' : ''}>
                    <Button block onClick={() => onFree()} loading={freeLoading} disabled={blocked}>
                        {current?.plan?.is_free && current?.has_active_plan ? 'Current plan' : 'Choose Free'}
                    </Button>
                </Tooltip>
            )
        }
        const quote = quotes[plan.id]
        if (!quote) return <Button block disabled>…</Button>
        const label = quote.kind === 'renew' ? `Renew for ${rupees(quote.amount)}`
            : quote.kind === 'upgrade' ? `Upgrade for ${rupees(quote.amount)}`
                : `Pay ${rupees(quote.amount)}`
        return (
            <Tooltip title={quote.allowed ? '' : quote.reason}>
                <Button
                    block
                    type="primary"
                    disabled={!quote.allowed || !current?.payments_enabled}
                    loading={paying === plan.id}
                    onClick={() => pay(plan)}
                >
                    {label}
                </Button>
            </Tooltip>
        )
    }

    return (
        <>
            <Radio.Group value={cycle} onChange={(e) => setCycle(e.target.value)} className="mb-4" buttonStyle="solid">
                <Radio.Button value="monthly">Monthly</Radio.Button>
                <Radio.Button value="yearly">Yearly {discount > 0 && <Tag color="green" className="ms-1 me-0">Save {discount}%</Tag>}</Radio.Button>
            </Radio.Group>

            <Spin spinning={loading}>
                <Row gutter={[16, 16]}>
                    {plans.map((plan) => {
                        const price = cycle === 'yearly' ? plan.yearly_price : plan.monthly_price
                        const quote = quotes[plan.id]
                        const isCurrent = current?.plan?.id === plan.id
                        return (
                            <Col xs={24} sm={12} lg={6} key={plan.id}>
                                {/* the card fills the row height and its button sits at the bottom, so all buttons line up */}
                                <Card
                                    className={`plan-card ${isCurrent && current?.has_active_plan ? 'plan-card-current' : ''}`}
                                    style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    title={<>{plan.name} {isCurrent && <Tag className="ms-1">{current.has_active_plan ? 'Current' : 'Your last plan'}</Tag>}</>}
                                    bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}
                                >
                                    <div style={{ fontSize: 28, fontWeight: 'bold' }}>
                                        {plan.is_free ? 'Free' : rupees(price)}
                                        {!plan.is_free && <span style={{ fontSize: 14, fontWeight: 'normal', color: '#888' }}> / {cycle === 'yearly' ? 'year' : 'month'}</span>}
                                    </div>
                                    {!plan.is_free && cycle === 'yearly' && (
                                        <div className="text-muted" style={{ fontSize: 13 }}>{rupees(plan.monthly_price * 12)} without the yearly discount</div>
                                    )}
                                    <div>Up to <b>{plan.active_clients.toLocaleString('en-IN')}</b> active clients</div>
                                    <div className="text-muted" style={{ fontSize: 13 }}>
                                        {plan.is_free ? 'Never expires'
                                            : quote?.kind === 'renew' ? `Adds a ${cycle === 'yearly' ? 'year' : 'month'}: until ${moment(quote.ends_at).format('DD MMM YYYY')}`
                                                : quote?.kind === 'upgrade' ? `${rupees(quote.credit)} credit from your current plan`
                                                    : `For one ${cycle === 'yearly' ? 'year' : 'month'}`}
                                    </div>
                                    <div style={{ marginTop: 'auto', paddingTop: 8 }}>{buttonFor(plan)}</div>
                                </Card>
                            </Col>
                        )
                    })}
                </Row>
            </Spin>
            {current && !current.payments_enabled && (
                <p className="text-muted mt-3">Online payment is turned off right now, so paid plans can't be bought. Please try again later.</p>
            )}
        </>
    )
}
